// Copyright (c) StrangeBee 2023

import React, { ReactElement } from 'react';
import { ExtractProps } from '@Molecules/Details/Details';
import {
    CaseContributor,
    CaseOutput,
    PAPValues,
    SeverityValues,
    TLPValues,
    SharedOrgOutput,
    UserOutput,
    TaskStatus,
    CustomStatusMapType,
    PermissionsEnum,
    OrganisationLinksOutput,
    OrganisationOutput,
} from 'thehive-sdk';
import { CaseEventList } from '@Organisms/Case/eventList';
import AssigneeInfo from '@Molecules/AssigneeInfo/AssigneeInfo';
import { TaskStatusColors } from '@Constants/colors';
import { Avatar } from 'antd';
import OwnerIcon from '@Atoms/OwnerIcon/OwnerIcon';
import DisplayEntityInfo from '@Organisms/DisplayEntityInfo/DisplayEntityInfo';
import { THEditable } from '@Molecules/Inputs/THEditable/THEditable';
import { THSecurityTag } from '@Molecules/Inputs/THSecurityTag/THSecurityTag';
import { SecurityTagType } from '@Types';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { THDatePicker } from '@Molecules/Inputs/THDatePicker/THDatePicker';
import { normalizeDate } from '@Helpers/date';
import { Select } from 'antd';
import { CustomColorLabel } from '@Atoms/CustomColorLabel/CustomColorLabel';
import { UseLocalMessageType } from '@Helpers/locale';
import { THUserSelector } from '@Molecules/Inputs/THUserSelector/THUserSelector';
import { FormatDate } from '@Molecules/Format';
import { PermissionExtract } from '@Hooks/useSplitPermissions';
import { SelectValue } from 'antd/lib/select';
import { THOrganisationSelector } from '@Molecules/Inputs/THOrganisationSelector/THOrganisationSelector';

import './extractData.scss';

const { OptGroup } = Select;

export const extractData = (
    caseId: string,
    hasPermissions: Record<
        PermissionExtract<[PermissionsEnum.MANAGE_CASE_UPDATE, PermissionsEnum.MANAGE_CASE_CHANGE_OWNERSHIP]>,
        boolean
    >,
    eventList: CaseEventList,
    locale: UseLocalMessageType,
): Record<any, ExtractProps> => ({
    '#computeSecurity': {
        render: (values: { tlp: TLPValues; pap: PAPValues; severity: SeverityValues }) => (
            <div className="case-details__security">
                <div className="severity">
                    <THEditable<Partial<CaseOutput>>
                        key={'severity'}
                        name={'severity'}
                        initialValues={{ ['severity']: values['severity'] }}
                        onFinish={(values) => eventList.onEdit(values, caseId)}
                        submitOnChange
                    >
                        <THSecurityTag
                            type={'severity' as SecurityTagType}
                            disabled={!hasPermissions.updateCase}
                            hasFullSeverityLabel
                        />
                    </THEditable>
                </div>
                <div className="pap_tlp">
                    {Object.entries(values)
                        .filter(([key]) => key !== 'severity')
                        .map(([key, value]) => (
                            <THEditable<Partial<CaseOutput>>
                                key={key}
                                name={key}
                                initialValues={{ [key]: value }}
                                onFinish={(values) => eventList.onEdit(values, caseId)}
                                submitOnChange
                            >
                                <THSecurityTag
                                    type={key as SecurityTagType}
                                    disabled={!hasPermissions.updateCase}
                                    hasFullSeverityLabel
                                />
                            </THEditable>
                        ))}
                </div>
            </div>
        ),
    },
    '#computeOwner': {
        className: 'case-details__owner',
        render: ({
            owningOrganisation,
            organisationList,
            setOrganisation,
            currentOrganisation,
        }: {
            owningOrganisation?: OrganisationOutput;
            organisationList: OrganisationLinksOutput[];
            setOrganisation: (value: SelectValue) => void;
            currentOrganisation?: string;
        }): ReactElement => (
            <THEditable<Partial<CaseOutput>>
                name="owner"
                label={locale('case.owner.form.organisation.label')}
                initialValues={{ owner: owningOrganisation?.name }}
                withDrawer={true}
            >
                <THOrganisationSelector
                    allowClear={false}
                    organisations={organisationList}
                    disabled={!hasPermissions.changeOwnershipCase || currentOrganisation !== owningOrganisation?.name}
                    onChange={(value: SelectValue) => {
                        setOrganisation(value);
                    }}
                />
            </THEditable>
        ),
    },
    '#computeAssignee': {
        className: 'case-details__assignee',
        render: ({ assignableUsers, assignee }: { assignableUsers: UserOutput[]; assignee: string }): ReactElement => (
            <THEditable<Partial<CaseOutput>>
                name="assignee"
                label={locale('common.assignee')}
                initialValues={{ assignee }}
                onFinish={(values) =>
                    eventList.onEdit(
                        { ...values, assignee: 'assignee' in values && !values.assignee ? null : values.assignee },
                        caseId,
                    )
                }
            >
                <THUserSelector
                    allowClear={false}
                    placeholder={locale('general.unassigned')}
                    users={assignableUsers}
                    disabled={!hasPermissions.updateCase}
                />
            </THEditable>
        ),
    },
    '#computeStatus': {
        className: 'case-detail__status',
        isForce: false,
        render: ({
            statusList,
            status,
            isOpen = false,
            impactStatus = 'YesImpact',
        }: {
            status: string;
            statusList: CustomStatusMapType;
            isOpen: boolean;
            impactStatus: string;
        }) => (
            <div className="extract-data__status-impact">
                <THEditable<Partial<CaseOutput>>
                    name="status"
                    label={locale('common.status')}
                    initialValues={{ status }}
                    onFinish={(values) => eventList.onEdit(values, caseId)}
                >
                    <THSelect borderless disabled={!isOpen || !hasPermissions.updateCase}>
                        {Object.entries(statusList).map(([key, values]) => (
                            <OptGroup key={key} label={key}>
                                {values.map(({ value, colour }) => (
                                    <THSelect.Option key={value} value={value}>
                                        <CustomColorLabel value={value} color={colour} />
                                    </THSelect.Option>
                                ))}
                            </OptGroup>
                        ))}
                    </THSelect>
                </THEditable>
                {!isOpen && (
                    <THEditable
                        name="impactStatus"
                        label={locale('case.close.form.impactStatus.label')}
                        initialValues={{ impactStatus }}
                    >
                        <span className="status-impact__impact-label">
                            {
                                {
                                    WithImpact: locale('general.yes'),
                                    NoImpact: locale('general.no'),
                                }[impactStatus]
                            }
                        </span>
                    </THEditable>
                )}
            </div>
        ),
    },
    startDate: {
        className: 'case-details__start-date',
        isForce: true,
        render: (startDate: number) => (
            <THEditable<Partial<CaseOutput>>
                name="startDate"
                label={locale('common.start-date')}
                initialValues={{ startDate }}
                onFinish={(values) =>
                    eventList.onEdit(
                        {
                            startDate: values.startDate || 0,
                        },
                        caseId,
                    )
                }
                formItemProps={{ normalize: normalizeDate }}
            >
                <THDatePicker
                    borderless
                    disabled={!hasPermissions.updateCase}
                    placeholder="Enter a start date..."
                    allowClear={false}
                />
            </THEditable>
        ),
    },
    endDate: {
        className: 'case-details__end-date',
        render: (endDate: number) => (
            <THEditable<Partial<CaseOutput>>
                name="endDate"
                label={locale('common.end-date')}
                initialValues={{ endDate }}
                onFinish={(values) => eventList.onEdit(values, caseId)}
                formItemProps={{ normalize: normalizeDate }}
            >
                <THDatePicker borderless disabled />
            </THEditable>
        ),
    },
    '#computeCount': {
        name: locale('common.task-completion'),
        isForce: true,
        className: 'case-details__compute-count',
        render: ({ taskStats: { total: taskCount = 0, ...stats } }: any): ReactElement =>
            taskCount ? (
                <DisplayEntityInfo
                    label=""
                    stats={
                        taskCount
                            ? Object.entries(stats)
                                  .sort(([a], [b]) => b.localeCompare(a))
                                  .map(([status, count]) => ({
                                      count: count as number,
                                      color: TaskStatusColors[status as TaskStatus],
                                      description: status,
                                  }))
                            : undefined
                    }
                />
            ) : (
                <>{locale('task.empty')}</>
            ),
    },
    '#computeContributors': {
        name: locale('common.contributors'),
        isForce: false,
        className: 'case-details__compute-contributors',
        render: ({ contributors }: { contributors: CaseContributor[] }): ReactElement => (
            <div className="case-details__compute-contributors__container">
                <Avatar.Group size="large">
                    {contributors.map(({ login }) => (
                        <AssigneeInfo key={login} assignee={login} avatarOnly placement="top" />
                    ))}
                </Avatar.Group>
            </div>
        ),
    },
    'extraData.isOwner': {
        className: 'case-details__owner',
        render: (extraData: any): ReactElement => <OwnerIcon isOwner={extraData?.isOwner} showText={true} />,
    },
    '#computeShareList': {
        name: locale('case.data.shared-with'),
        className: 'case-details__shares',
        isForce: false,
        render: (shareList: SharedOrgOutput[]): ReactElement => (
            <>
                {shareList.map(({ organisationName, profileName }) => (
                    <div key={organisationName}>
                        {organisationName} ({profileName})
                    </div>
                ))}
            </>
        ),
    },
    'extraData.computed.handlingDurationInSeconds': {
        render: (duration: number) => <>{duration}</>,
    },
    timeToDetect: {
        name: locale('common.timeToDetect'),
        isForce: false,
        render: (timeToDetect: number) => <FormatDate date={timeToDetect} onlyFrom endDate={0} withoutSuffix />,
    },
    timeToAcknowledge: {
        name: locale('common.timeToAcknowledge'),
        isForce: false,
        render: (timeToAcknowledge: number) => (
            <FormatDate date={timeToAcknowledge} onlyFrom endDate={0} withoutSuffix />
        ),
    },
    timeToResolve: {
        name: locale('common.timeToResolve'),
        isForce: false,
        render: (timeToResolve: number) => <FormatDate date={timeToResolve} onlyFrom endDate={0} withoutSuffix />,
    },
    timeToTriage: {
        name: locale('common.timeToTriage'),
        isForce: false,
        render: (timeToTriage: number) => <FormatDate date={timeToTriage} onlyFrom endDate={0} withoutSuffix />,
    },
    timeToQualify: {
        name: locale('common.timeToQualify'),
        isForce: false,
        render: (timeToQualify: number) => <FormatDate date={timeToQualify} onlyFrom endDate={0} withoutSuffix />,
    },
});
